import { Component, OnInit, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

interface Message {
  id: number;
  senderId: number;
  senderName: string;
  senderAvatar: string;
  content: string;
  timestamp: Date;
  isRead: boolean;
}

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit, AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  messageForm: FormGroup;
  messages: Message[] = [];
  currentClientId: number | null = null;
  currentClientName: string = '';
  currentClientAvatar: string = '';
  isOnline: boolean = true;

  // Dados fake para demonstração
  private readonly FAKE_CLIENT = {
    id: 1,
    name: 'Maria Silva',
    avatar: 'assets/images/user/user-default.png',
    messages: [
      {
        id: 1,
        senderId: 1,
        senderName: 'Maria Silva',
        senderAvatar: 'assets/images/user/user-default.png',
        content: 'Olá! Gostaria de saber mais sobre o processo de cidadania italiana.',
        timestamp: new Date(Date.now() - 3600000), // 1 hora atrás
        isRead: true
      },
      {
        id: 2,
        senderId: 0,
        senderName: 'Você',
        senderAvatar: 'assets/images/avatars/user-avatar.png',
        content: 'Olá Maria! Claro, ficarei feliz em ajudar. O processo de cidadania italiana pode ser feito por descendência ou por casamento. Qual é o seu caso?',
        timestamp: new Date(Date.now() - 3540000), // 59 minutos atrás
        isRead: true
      },
      {
        id: 3,
        senderId: 1,
        senderName: 'Maria Silva',
        senderAvatar: 'assets/images/user/user-default.png',
        content: 'É por descendência. Meu bisavô era italiano.',
        timestamp: new Date(Date.now() - 1800000), // 30 minutos atrás
        isRead: true
      }
    ]
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute
  ) {
    this.messageForm = this.fb.group({
      message: ['', [Validators.required, Validators.minLength(1)]]
    });
  }

  ngOnInit() {
    // Obter o ID do cliente da URL
    this.route.queryParams.subscribe(params => {
      const clienteId = params['clienteId'];
      if (clienteId) {
        this.loadClientData(clienteId);
        this.loadMessages(clienteId);
      } else {
        // Se não houver clienteId, carrega a conversa fake
        this.loadFakeConversation();
      }
    });
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  private loadFakeConversation() {
    this.currentClientId = this.FAKE_CLIENT.id;
    this.currentClientName = this.FAKE_CLIENT.name;
    this.currentClientAvatar = this.FAKE_CLIENT.avatar;
    this.messages = [...this.FAKE_CLIENT.messages];
    this.isOnline = true;
  }

  private loadClientData(clienteId: number) {
    // TODO: Implementar chamada à API para carregar dados do cliente
    this.currentClientId = clienteId;
    this.currentClientName = 'Nome do Cliente'; // Substituir pelo nome real do cliente
    this.currentClientAvatar = 'assets/images/avatars/default-avatar.png'; // Substituir pelo avatar real
    this.isOnline = true;
  }

  private loadMessages(clienteId: number) {
    // TODO: Implementar chamada à API para carregar mensagens
    // Por enquanto, usando dados de exemplo
    this.messages = [
      {
        id: 1,
        senderId: clienteId,
        senderName: this.currentClientName,
        senderAvatar: this.currentClientAvatar,
        content: 'Olá, como posso ajudar?',
        timestamp: new Date(Date.now() - 3600000),
        isRead: true
      },
      {
        id: 2,
        senderId: 0, // ID do usuário atual
        senderName: 'Você',
        senderAvatar: 'assets/images/avatars/user-avatar.png',
        content: 'Preciso de informações sobre o processo.',
        timestamp: new Date(Date.now() - 1800000),
        isRead: true
      }
    ];
  }

  sendMessage() {
    if (this.messageForm.valid && this.currentClientId) {
      const messageContent = this.messageForm.get('message')?.value;
      
      // TODO: Implementar chamada à API para enviar mensagem
      const newMessage: Message = {
        id: this.messages.length + 1,
        senderId: 0, // ID do usuário atual
        senderName: 'Você',
        senderAvatar: 'assets/images/avatars/user-avatar.png',
        content: messageContent,
        timestamp: new Date(),
        isRead: true
      };

      this.messages.push(newMessage);
      this.messageForm.reset();
      this.scrollToBottom();

      // Simula uma resposta automática após 2 segundos
      setTimeout(() => {
        const responseMessage: Message = {
          id: this.messages.length + 1,
          senderId: this.currentClientId!,
          senderName: this.currentClientName,
          senderAvatar: this.currentClientAvatar,
          content: 'Entendi! Vou analisar seu caso e retornar em breve com mais informações.',
          timestamp: new Date(),
          isRead: true
        };
        this.messages.push(responseMessage);
        this.scrollToBottom();
      }, 2000);
    }
  }

  private scrollToBottom(): void {
    try {
      const element = this.messagesContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
    } catch (err) {
      console.error('Erro ao rolar para o final da conversa:', err);
    }
  }

  formatMessageTime(date: Date): string {
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatMessageDate(date: Date): string {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Hoje';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ontem';
    } else {
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  }
} 